import { useCallback, useState, forwardRef } from 'react';
import { Input } from '../../../../../components/ui/input';
import { Switch } from '../../../../../components/ui/switch';
import type { DataTypeCategory } from '../../utils/filterUtils';
import { MAX_EXPANDED_DIGITS, numberFieldValue } from '../../utils/sqlNumericLiteral';
import { safeToDatetimeLocal } from '../../../../../utils/dateUtils';
import type { TableExplorerCellValueCodec } from '../../../table-explorer/components/tableExplorerHostPorts';
import { CodecFilterValueInput, type CodecFilterValue } from './CodecFilterValueInput';

type FilterValueInputProps = {
  value: string | number | boolean | Date | null | undefined;
  onChange: (value: string | number | boolean | Date | null) => void;
  dataTypeCategory: DataTypeCategory;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onBlur?: () => void;
  autoFocus?: boolean;
  'aria-label'?: string;
};

const CategoryFilterValueInput = forwardRef<HTMLInputElement, FilterValueInputProps>(
  function CategoryFilterValueInput(
    {
      value,
      onChange,
      dataTypeCategory,
      // PP-11 — "Filter…" everywhere a filter's value input renders, so the
      // wording never implies this is a data-entry field rather than a
      // search/filter control. This is the shared default every FilterChip /
      // DraftFilterChip / JsonFilterFields value input falls back to when it
      // doesn't pass its own `placeholder` (ImportFilterChip does, and keeps
      // its own copy — it isn't a filter-row surface).
      placeholder = 'Filter…',
      disabled = false,
      className = '',
      onKeyDown,
      onBlur,
      autoFocus = false,
      'aria-label': ariaLabel,
    },
    ref
  ) {
  // Handle string input change
  const handleStringChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  // The number field's text as typed. `value` is its converted form, and
  // `String(value)` drops what the conversion does not keep (`0.0` is `0`):
  // re-rendered on every key, typing `0.01` committed `1`. The typed text
  // shows while it still converts to `value`; a value set from outside (a
  // reopened filter, Clear) shows as itself.
  const [typed, setTyped] = useState('');

  // Handle number input change. A number a JS number cannot hold exactly (a
  // 64-bit key past 2^53) keeps its text — the emitters write it bare on a
  // numeric column (`sqlNumericLiteral.ts`) — rather than rounding to another
  // value; a number spelled past the digit limit is refused (`null`).
  const handleNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setTyped(val);
      onChange(val === '' ? null : numberFieldValue(val));
    },
    [onChange]
  );

  // Handle datetime-local input change
  const handleDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (val) {
        // datetime-local gives "YYYY-MM-DDThh:mm[:ss]" (zero seconds are
        // omitted). Written as picked: an engine that needs another form gets
        // it at emit time from `sqlTemporalLiteral` (SQL Server's seconds,
        // Oracle's TO_DATE), for a known date column only — a SQLite text
        // date `'2024-03-01T00:00'` must still match the minute picked.
        onChange(val);
      } else {
        onChange(null);
      }
    },
    [onChange]
  );

  // Handle boolean toggle
  const handleBooleanChange = useCallback(
    (checked: boolean) => {
      onChange(checked);
    },
    [onChange]
  );

  // Format value for input
  const getInputValue = (): string => {
    if (value === null || value === undefined) return '';
    if (value instanceof Date) {
      return safeToDatetimeLocal(value);
    }
    return String(value);
  };

  // Render based on data type category
  switch (dataTypeCategory) {
    case 'boolean': {
      // Callers may hand the value over as a real boolean (Table Explorer) or as
      // its string form (Data Import filter conditions store raw strings) —
      // strict Boolean('false') would read as true, sticking the switch ON.
      const boolValue = value === true || value === 'true' ? true : value === false || value === 'false' ? false : null;
      return (
        <div className={`flex items-center gap-2 ${className}`}>
          <Switch
            checked={boolValue === true}
            onCheckedChange={handleBooleanChange}
            disabled={disabled}
            aria-label={ariaLabel}
          />
          <span className="text-xs text-muted-foreground">
            {boolValue === true ? 'True' : boolValue === false ? 'False' : 'Not set'}
          </span>
        </div>
      );
    }

    case 'number': {
      const typedValue = typed === '' ? null : numberFieldValue(typed);
      const showTyped = String(typedValue ?? '') === String(value ?? '');
      // Typed text the field refuses (an exponent past the digit limit): no
      // value, shown invalid, and Enter does not apply it.
      const refused = showTyped && typed !== '' && typedValue === null;
      return (
        <Input
          ref={ref}
          type="number"
          inputMode="decimal"
          autoComplete="off"
          // A boolean on a number column (MySQL's `tinyint(1)` flag filtered
          // with `TRUE`, or a filter persisted with a JS boolean) shows the
          // 1 / 0 it compares as — the number field cannot display "true".
          value={typeof value === 'boolean' ? (value ? '1' : '0') : showTyped ? typed : getInputValue()}
          onChange={handleNumberChange}
          onKeyDown={(e) => {
            if (refused && e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
            } else onKeyDown?.(e);
          }}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-label={ariaLabel}
          aria-invalid={refused || undefined}
          title={refused ? `Too many digits: at most ${MAX_EXPANDED_DIGITS} when written out in full` : undefined}
          className={`h-7 text-xs ${refused ? 'border-destructive/60 focus-visible:ring-destructive/40' : ''} ${className}`}
        />
      );
    }

    case 'datetime':
      return (
        <Input
          ref={ref}
          type="datetime-local"
          step="1"
          autoComplete="off"
          value={getInputValue()}
          onChange={handleDateChange}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-label={ariaLabel}
          className="h-7 text-xs w-auto"
        />
      );

    case 'string':
    default:
      return (
        <Input
          ref={ref}
          type="text"
          autoComplete="off"
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          value={getInputValue()}
          onChange={handleStringChange}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-label={ariaLabel}
          className={`h-7 text-xs ${className}`}
        />
      );
    }
  }
);

/**
 * A host value codec (Azure Table, S4 review D, D3) gets the draft-text `CodecFilterValueInput`; without one —
 * every SQL engine — the category-driven input above is used exactly as before.
 */
export const FilterValueInput = forwardRef<
  HTMLInputElement,
  Omit<FilterValueInputProps, 'value' | 'onChange'> & {
    value: CodecFilterValue | undefined;
    onChange: (value: CodecFilterValue) => void;
    valueCodec?: TableExplorerCellValueCodec;
    valueTag?: string;
    onValidityChange?: (error: string | undefined) => void;
  }
>(function FilterValueInput({ valueCodec, valueTag, onValidityChange, value, onChange, dataTypeCategory, ...rest }, ref) {
  if (valueCodec) {
    return <CodecFilterValueInput ref={ref} {...rest} value={value} onChange={onChange} valueCodec={valueCodec} valueTag={valueTag} onValidityChange={onValidityChange} />;
  }
  return <CategoryFilterValueInput ref={ref} {...rest} dataTypeCategory={dataTypeCategory} value={value as FilterValueInputProps['value']} onChange={onChange} />;
});
